import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import DOMPurify from "dompurify";
import Modal from "react-modal";
// import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import units from "../../../utils/units";
import "./windowComps.scss";
import {
  adjustModalPositionAndSize,
  // logToNormal,
  // normalToLog,
  hasProperty,
  convertRangeFromUnitsToUnits,
} from "../../../utils/helpers";
import { deepCopy } from "../../leftSide/Models/modelLogic";
import { GeneralContext } from "../../../context/GeneralContext";
//import { GraphContext } from "../../../context/GraphContext";
import { useTutorialProgress } from "../../../context/TutorialContext";
import CustomButton from "../../commonComponents/CustomButton";
import CloseButton from "../../commonComponents/CloseButton";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";

function OptionsModal(props) {
  const {
    settingsModalIsOpen,
    setSettingsModalIsOpen,
    reference,
    initialLayout,
    graphRef,
    updateGraphLayout,
    id,
    zIndex,
    setXAxisUpdated,
    type = "standard",
  } = props;

  const { recordedErrorLog } = useContext(GeneralContext);
  const { monitorStateChange } = useTutorialProgress();

  // Initialize all state with simple defaults - will be set correctly when modal opens
  const [isXAxisLogTemp, setIsXAxisLogTemp] = useState(false);
  const [isYAxisLogTemp, setIsYAxisLogTemp] = useState(false);
  const [xAxisNameTemp, setXAxisNameTemp] = useState("");
  const [yAxisNameTemp, setYAxisNameTemp] = useState("");
  const [graphNameTemp, setGraphNameTemp] = useState("");
  const [xUnitTemp, setXUnitTemp] = useState(units[0]);
  const [multiplyBy100Temp, setMultiplyBy100Temp] = useState(false);
  const [hideXAxisUnitsTemp, setHideXAxisUnitsTemp] = useState(false);
  const [hideYAxisUnitsTemp, setHideYAxisUnitsTemp] = useState(false);
  const [xAxisRangeTemp, setXAxisRangeTemp] = useState({
    min: 0,
    max: 100,
  });
  const [yAxisRangeTemp, setYAxisRangeTemp] = useState({ min: 0, max: 100 });
  const [modalPlaceAndSize, setModalPlaceAndSize] = useState({
    top: "0",
    left: "0",
    height: "420px",
    width: "300px",
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const [graphNameError, setGraphNameError] = useState(false);
  const [xAxisRangeError, setXAxisRangeError] = useState(false);
  const [yAxisRangeError, setYAxisRangeError] = useState(false);
  // Helper to get current graph layout for initialization
  const getCurrentGraphLayout = () => {
    const currentGraph = graphRef.current?.find((graph) => graph.id == id);
    return currentGraph?.layout || initialLayout;
  };

  // Helper functions to get initial values from graph
  const getInitialXUnit = () => {
    try {
      const currentGraphLayout = getCurrentGraphLayout();
      return currentGraphLayout.xaxis?.unit || units[0];
    } catch (error) {
      console.error("Error getting initial xUnit:", error);
      return units[0];
    }
  };

  const getInitialMultiplyBy100 = () => {
    try {
      const currentGraphLayout = getCurrentGraphLayout();
      return currentGraphLayout.yaxis?.multiplyBy100 || false;
    } catch (error) {
      console.error("Error getting initial multiplyBy100:", error);
      return false;
    }
  };

  const getInitialHideAxisUnits = (axis) => {
    try {
      const currentGraphLayout = getCurrentGraphLayout();
      if (axis === "x") {
        return currentGraphLayout.xaxis?.hideUnits || false;
      } else if (axis === "y") {
        return currentGraphLayout.yaxis?.hideUnits || false;
      }
      return false;
    } catch (error) {
      console.error("Error getting initial hideAxisUnits:", error);
      return false;
    }
  };

  const getInitialRangeTemp = (axis) => {
    try {
      const layout = getCurrentGraphLayout();
      const range = axis === "x" ? layout.xaxis?.range : layout.yaxis?.range;
      if (range && range[0] !== undefined && range[1] !== undefined) {
        return { min: range[0], max: range[1] };
      }
      return { min: 0, max: 100 };
    } catch (error) {
      console.error("Error getting initial rangeTemp:", error);
      return { min: 0, max: 100 };
    }
  };

  const getInitialLogLast = (axis) => {
    try {
      const layout = getCurrentGraphLayout();
      const axisType = axis === "x" ? layout.xaxis?.type : layout.yaxis?.type;
      return axisType === "log";
    } catch (error) {
      console.error("Error getting initial logLast:", error);
      return false;
    }
  };

  useEffect(() => {
    // Only initialize when modal opens
    try {
      if (settingsModalIsOpen) {
        const modalPosition = {
          top:
            reference.current != null
              ? reference.current.getBoundingClientRect().top
              : 0,
          right: "auto",
          left:
            reference.current != null
              ? reference.current.getBoundingClientRect().left
              : 0,
        };
        const modalSize = { width: 650, height: 430 };

        const adjusted = adjustModalPositionAndSize(modalPosition, modalSize);

        setModalPlaceAndSize(adjusted);

        // Initialize all temp values from current graph
        const layout = getCurrentGraphLayout();
        const currentGraph = graphRef.current?.find((graph) => graph.id == id);

        // Extract base axis names (remove unit in brackets if present)
        const extractBaseAxisName = (titleText) => {
          if (!titleText) return "";
          const sanitized = DOMPurify.sanitize(titleText, { ALLOWED_TAGS: [] });
          return sanitized.replace(/\s*\[.*?\]\s*$/, "").trim();
        };

        const xAxisTitleText = layout.xaxis?.title?.text || "";
        const yAxisTitleText = layout.yaxis?.title?.text || "";
        setXAxisNameTemp(extractBaseAxisName(xAxisTitleText));
        setYAxisNameTemp(extractBaseAxisName(yAxisTitleText));

        setGraphNameTemp(currentGraph?.title || "");
        const initialXUnit = getInitialXUnit();
        setXUnitTemp(initialXUnit);
        setMultiplyBy100Temp(getInitialMultiplyBy100());
        setHideXAxisUnitsTemp(getInitialHideAxisUnits("x"));
        setHideYAxisUnitsTemp(getInitialHideAxisUnits("y"));

        // Initialize range temps from current graph layout
        const newXRangeTemp = getInitialRangeTemp("x");
        const newYRangeTemp = getInitialRangeTemp("y");

        setXAxisRangeTemp(newXRangeTemp);
        setYAxisRangeTemp(newYRangeTemp);

        // Initialize log "last" states from current graph layout
        const xIsLogLast = getInitialLogLast("x");
        const yIsLogLast = getInitialLogLast("y");
        setIsXAxisLogTemp(xIsLogLast);
        setIsYAxisLogTemp(yIsLogLast);

        // Reset validation errors
        setGraphNameError(false);
        setXAxisRangeError(false);
        setYAxisRangeError(false);
      }
    } catch (error) {
      recordedErrorLog("Error initializing options modal:", error);
    }
  }, [settingsModalIsOpen]);

  // Validates values
  const validateGraphName = (name) => {
    try {
      if (name.trim() === "") {
        return false;
      }
      return true;
    } catch (error) {
      recordedErrorLog("Error validating graph name:", error);
      return false;
    }
  };

  const validateRange = (range, islog) => {
    try {
      if (islog && range.min <= 0) {
        return false;
      }
      if (range.min >= range.max) {
        return false;
      }
      return true;
    } catch (error) {
      recordedErrorLog("Error validating range:", error);
      return false;
    }
  };

  // Validation effects
  useEffect(() => {
    try {
      setGraphNameError(!validateGraphName(graphNameTemp));
    } catch (error) {
      recordedErrorLog("Error validating graph name:", error);
    }
  }, [graphNameTemp]);

  useEffect(() => {
    try {
      setXAxisRangeError(!validateRange(xAxisRangeTemp, isXAxisLogTemp));
    } catch (error) {
      recordedErrorLog("Error validating x axis range:", error);
    }
  }, [xAxisRangeTemp, isXAxisLogTemp]);

  useEffect(() => {
    try {
      setYAxisRangeError(!validateRange(yAxisRangeTemp, isYAxisLogTemp));
    } catch (error) {
      recordedErrorLog("Error validating y axis range:", error);
    }
  }, [yAxisRangeTemp, isYAxisLogTemp]);

  // Handlers for changing internal values
  const handleGraphNameChange = (e) => {
    try {
      setGraphNameTemp(e.target.value);
    } catch (error) {
      recordedErrorLog("Error handling graph name change:", error);
    }
  };

  const handleXAxisNameChange = (e) => {
    try {
      setXAxisNameTemp(e.target.value);
    } catch (error) {
      recordedErrorLog("Error handling x axis name change:", error);
    }
  };

  const handleYAxisNameChange = (e) => {
    try {
      setYAxisNameTemp(e.target.value);
    } catch (error) {
      recordedErrorLog("Error handling y axis name change:", error);
    }
  };

  const handleXUnitChange = (e) => {
    try {
      const oldXUnitTemp = xUnitTemp;
      const value = e.target.value;
      let newUnit = units.filter((u) => u.value === value)[0];
      if (!newUnit) {
        newUnit = units[0];
      }
      setXUnitTemp(newUnit);

      // Ensure we have valid numbers
      let min = isNaN(xAxisRangeTemp.min) ? 0 : xAxisRangeTemp.min;
      let max = isNaN(xAxisRangeTemp.max) ? min + 100 : xAxisRangeTemp.max;

      // Convert range object to array for the conversion function
      const rangeArray = [min, max];
      const convertedRange = convertRangeFromUnitsToUnits(
        oldXUnitTemp,
        newUnit,
        rangeArray
      );

      // Convert back to object and ensure values are valid numbers
      const convertedMin =
        typeof convertedRange[0] === "number" && !isNaN(convertedRange[0])
          ? convertedRange[0]
          : min;
      const convertedMax =
        typeof convertedRange[1] === "number" && !isNaN(convertedRange[1])
          ? convertedRange[1]
          : max;

      setXAxisRangeTemp({ min: convertedMin, max: convertedMax });
    } catch (error) {
      recordedErrorLog("Error handling x unit change:", error);
    }
  };

  const handleMultiplyBy100Change = (e) => {
    try {
      const isChecked = e.target.checked;
      // When checked: multiply by 100, when unchecked: divide by 100
      const conversionFactor = isChecked ? 100 : 0.01;
      setYAxisRangeTemp((old) => {
        return {
          ...old,
          min: old.min * conversionFactor,
          max: old.max * conversionFactor,
        };
      });
      setMultiplyBy100Temp(isChecked);
    } catch (error) {
      recordedErrorLog("Error handling multiply by 100 change:", error);
    }
  };

  const handleAxisRangeChange = (axis, minOrMax, val) => {
    try {
      // Convert string value to float, handling empty strings and invalid values
      const numVal =
        val === "" || val === null || val === undefined ? val : parseFloat(val);
      // If parseFloat returns NaN, keep the original value (might be partial input like "-" or ".")
      const finalVal = isNaN(numVal) ? val : numVal;

      if (axis === "x") {
        if (minOrMax === "min") {
          setXAxisRangeTemp((old) => {
            return { ...old, min: finalVal };
          });
        } else {
          setXAxisRangeTemp((old) => {
            return { ...old, max: finalVal };
          });
        }
      } else {
        if (minOrMax === "min") {
          setYAxisRangeTemp((old) => {
            return { ...old, min: finalVal };
          });
        } else {
          setYAxisRangeTemp((old) => {
            return { ...old, max: finalVal };
          });
        }
      }
    } catch (error) {
      recordedErrorLog("Axis range change failure: ", error);
    }
  };

  const handleLogChange = (axis, isChecked) => {
    try {
      if (axis === "x") {
        setIsXAxisLogTemp(isChecked);
      } else {
        setIsYAxisLogTemp(isChecked);
      }
      if (isChecked) {
        if (axis === "x") {
          setXAxisRangeTemp((old) => {
            return {
              min: old.min <= 0 ? 0.1 : old.min,
              max: old.max <= 0 ? 0.2 : old.max,
            };
          });
        } else {
          setYAxisRangeTemp((old) => {
            return {
              min: old.min <= 0 ? 0.1 : old.min,
              max: old.max <= 0 ? 0.2 : old.max,
            };
          });
        }
      }
    } catch (error) {
      recordedErrorLog("Error handling log change:", error);
    }
  };

  // Apply and Cancel functions
  const confirmSettingsModal = () => {
    try {
      let updateNeeded = false;
      let xAxisChanged = false;
      const currentGraph = deepCopy(
        graphRef.current.find((graph) => graph.id == id)
      );

      if (
        graphNameTemp.trim() !== currentGraph.title &&
        validateGraphName(graphNameTemp.trim())
      ) {
        updateNeeded = true;
        currentGraph.title = graphNameTemp.trim();
      }

      if (xAxisNameTemp.trim() !== currentGraph.layout.xaxis.title.text) {
        updateNeeded = true;
        currentGraph.layout.xaxis.title.text = xAxisNameTemp.trim();
      }

      if (yAxisNameTemp.trim() !== currentGraph.layout.yaxis.title.text) {
        updateNeeded = true;
        currentGraph.layout.yaxis.title.text = yAxisNameTemp.trim();
      }

      if (
        validateRange(xAxisRangeTemp, isXAxisLogTemp) &&
        ((isXAxisLogTemp && currentGraph.layout.xaxis.type === "linear") ||
          (!isXAxisLogTemp && currentGraph.layout.xaxis.type === "log"))
      ) {
        updateNeeded = true;
        xAxisChanged = true;
        currentGraph.layout.xaxis.type = isXAxisLogTemp ? "log" : "linear";
        currentGraph.layout.xaxis.autorange = false;
      }

      if (
        validateRange(yAxisRangeTemp, isYAxisLogTemp) &&
        ((isYAxisLogTemp && currentGraph.layout.yaxis.type === "linear") ||
          (!isYAxisLogTemp && currentGraph.layout.yaxis.type === "log"))
      ) {
        updateNeeded = true;
        currentGraph.layout.yaxis.type = isYAxisLogTemp ? "log" : "linear";
        currentGraph.layout.yaxis.autorange = false;
      }
      // Update graph xUnit
      const currentXUnit = currentGraph.layout.xaxis.unit || units[0];
      if (currentXUnit !== xUnitTemp) {
        xAxisChanged = true;
        updateNeeded = true;
        currentGraph.layout.xaxis.unit = xUnitTemp;
      }

      const multiplyBy100 = currentGraph.layout.yaxis.multiplyBy100 || false;
      if (multiplyBy100Temp !== multiplyBy100) {
        updateNeeded = true;
        currentGraph.layout.yaxis.multiplyBy100 = multiplyBy100Temp;
      }

      if (
        validateRange(xAxisRangeTemp, isXAxisLogTemp) &&
        (xAxisRangeTemp.min !== currentGraph.layout.xaxis.range[0] ||
          xAxisRangeTemp.max !== currentGraph.layout.xaxis.range[1])
      ) {
        xAxisChanged = true;
        updateNeeded = true;
        currentGraph.layout.xaxis.range = [
          xAxisRangeTemp.min,
          xAxisRangeTemp.max,
        ];
        currentGraph.layout.xaxis.autorange = false;
      }
      if (!hasProperty(currentGraph.layout.yaxis, "range")) {
        updateNeeded = true;
        currentGraph.layout.yaxis.range = [0, 100];
      }
      if (
        validateRange(yAxisRangeTemp, isYAxisLogTemp) &&
        (yAxisRangeTemp.min !== currentGraph.layout.yaxis.range[0] ||
          yAxisRangeTemp.max !== currentGraph.layout.yaxis.range[1])
      ) {
        updateNeeded = true;
        currentGraph.layout.yaxis.range = [
          yAxisRangeTemp.min,
          yAxisRangeTemp.max,
        ];
        currentGraph.layout.yaxis.autorange = false;
      }
      // Update graph hideXAxisUnits
      const currentHideXAxisUnits =
        currentGraph.layout.xaxis.hideUnits || false;
      if (hideXAxisUnitsTemp !== currentHideXAxisUnits) {
        currentGraph.layout.xaxis.hideUnits = hideXAxisUnitsTemp;
        updateNeeded = true;
      }

      // Update graph hideYAxisUnits
      const currentHideYAxisUnits =
        currentGraph.layout.yaxis.hideUnits || false;
      if (hideYAxisUnitsTemp !== currentHideYAxisUnits) {
        currentGraph.layout.yaxis.hideUnits = hideYAxisUnitsTemp;
        updateNeeded = true;
      }

      if (updateNeeded) {
        updateGraphLayout(currentGraph);
        monitorStateChange("graph_options_modal_applied", {
          currentGraph: currentGraph,
        });
      }
      if (xAxisChanged) {
        setXAxisUpdated(true);
      }
    } catch (error) {
      recordedErrorLog("Setting confirmation failure: ", error);
    }
  };

  const handleCloseSettingsModal = () => {
    try {
      setSettingsModalIsOpen(false);
      // reset temp values to the initial layout values

      setGraphNameTemp("graph");
      setXAxisNameTemp("");
      setYAxisNameTemp("");
      setXUnitTemp(units[0]);
      setMultiplyBy100Temp(false);
      setHideXAxisUnitsTemp(false);
      setHideYAxisUnitsTemp(false);

      // Reset range temps from current graph layout
      setXAxisRangeTemp({ min: 0, max: 100 });
      setYAxisRangeTemp({ min: 0, max: 100 });

      // Reset log "last" states
      setIsXAxisLogTemp(false);
      setIsYAxisLogTemp(false);
    } catch (error) {
      recordedErrorLog("Error handling close settings modal:", error);
    }
  };

  // Drag handlers
  const handleDragStart = (e) => {
    // Don't start drag if clicking on buttons or interactive elements
    if (
      e.target.closest("button") ||
      e.target.closest(".settingsModalHeaderButtons") ||
      e.target.closest("input") ||
      e.target.closest("select")
    ) {
      return;
    }

    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
      e.preventDefault();
    }
  };

  const confirmSettingsModalAndClose = () => {
    try {
      confirmSettingsModal();
      handleCloseSettingsModal();
    } catch (error) {
      recordedErrorLog("Error confirming settings modal and close:", error);
    }
  };

  const handleDrag = useCallback(
    (e) => {
      if (modalRef.current) {
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;

        // Keep modal within viewport bounds
        const maxLeft = window.innerWidth - modalRef.current.offsetWidth;
        const maxTop = window.innerHeight - modalRef.current.offsetHeight;

        const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
        const constrainedTop = Math.max(0, Math.min(newTop, maxTop));

        setModalPlaceAndSize((prev) => ({
          ...prev,
          left: `${constrainedLeft}px`,
          top: `${constrainedTop}px`,
          right: "auto",
        }));
      }
    },
    [dragOffset]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        e.preventDefault();
        handleDrag(e);
      };
      const handleMouseUp = () => {
        handleDragEnd();
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  const getGraphName = () => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      if (currentGraph) {
        return ": " + currentGraph.title;
      }
      return " ";
    } catch (error) {
      recordedErrorLog("Error getting graph name:", error);
      return " ";
    }
  };

  return (
    <Modal
      isOpen={settingsModalIsOpen}
      onRequestClose={handleCloseSettingsModal}
      shouldCloseOnOverlayClick={true}
      contentLabel="Model Modal"
      appElement={reference.current}
      style={{
        content: {
          width: modalPlaceAndSize.width,
          height: modalPlaceAndSize.height,
          top: modalPlaceAndSize.top,
          left: modalPlaceAndSize.left,
          right: modalPlaceAndSize.right,
          border: "1px solid black",
          borderRadius: "10px",
          padding: "0",
        },
        overlay: {
          backgroundColor: "transparent",
          zIndex: zIndex + 200,
        },
      }}
    >
      <div className="settingsModal" id="graph-settings-modal" ref={modalRef}>
        <div
          className="settingsModalHeader"
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <div className="settingsModalTitle">
            Graph Options{getGraphName()}
          </div>
          <div className="settingsModalHeaderButtons">
            <CustomButton
              text="Apply"
              extraClassnames={["noMargin"]}
              handleClick={confirmSettingsModal}
              disabled={graphNameError || xAxisRangeError || yAxisRangeError}
              id="graph-settings-apply-button"
            />
            <CustomButton
              text="Apply and Close"
              extraClassnames={["noMargin"]}
              handleClick={confirmSettingsModalAndClose}
              disabled={graphNameError || xAxisRangeError || yAxisRangeError}
              id="graph-settings-apply-and-close-button"
            />
            <CloseButton onClose={handleCloseSettingsModal} />
          </div>
        </div>
        <div className="graphNameSection">
          <div className="graphNameLabel">Graph Name:</div>
          <input
            id="graph-name-input"
            className="graphNameInput"
            value={graphNameTemp}
            onChange={handleGraphNameChange}
            style={graphNameError ? { border: "2px solid red" } : {}}
            type="text"
          />
        </div>
        <div className="axisGroup">
          <div className="x-data">
            <div className="axisName">X Axis</div>
            <div className="axisValues">
              <div className="axisValueSet">
                <div className="axisValueTitle">Name:</div>
                <input
                  id="x-axis-name-input"
                  className="axisInput"
                  value={xAxisNameTemp}
                  onChange={handleXAxisNameChange}
                />
              </div>
              <div className="axisValueSet">
                <div className="axisValueTitle">Min:</div>
                <TextField
                  className={`rangeValEntry rangeValTextField ${
                    xAxisRangeError ? "rangeValTextFieldError" : ""
                  }`}
                  value={xAxisRangeTemp.min}
                  onChange={(e) =>
                    handleAxisRangeChange("x", "min", e.target.value)
                  }
                  // onBlur={(e) =>
                  //   handleAxisRangeBlur("x", "min", e.target.value)
                  // }
                  size="small"
                  type="number"
                  id="x-axis-min-input"
                  style={xAxisRangeError ? { border: "2px solid red" } : {}}
                />
              </div>
              <div className="axisValueSet">
                <div className="axisValueTitle">Max:</div>
                <TextField
                  className={`rangeValEntry rangeValTextField ${
                    xAxisRangeError ? "rangeValTextFieldError" : ""
                  }`}
                  value={xAxisRangeTemp.max}
                  onChange={(e) =>
                    handleAxisRangeChange("x", "max", e.target.value)
                  }
                  // onBlur={(e) =>
                  //   handleAxisRangeBlur("x", "max", e.target.value)
                  // }
                  size="small"
                  type="number"
                  id="x-axis-max-input"
                  style={xAxisRangeError ? { border: "2px solid red" } : {}}
                />
              </div>
              {type === "standard" && (
                <div className="axisValueSet">
                  <div className="axisValueTitle">Units:</div>
                  <FormControl variant="standard" className="axisUnitSelect">
                    <Select
                      MenuProps={{
                        PaperProps: {
                          style: {
                            zIndex: 10000,
                          },
                        },
                      }}
                      labelId="selectXUnit"
                      value={xUnitTemp.value}
                      onChange={handleXUnitChange}
                      className="unitSelect"
                      size="small"
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onClick={() =>
                          setHideXAxisUnitsTemp(!hideXAxisUnitsTemp)
                        }
                        checked={hideXAxisUnitsTemp}
                      />
                    }
                    label="Hide"
                  />
                </div>
              )}
              <div className="axisValueSet">
                <FormControlLabel
                  control={
                    <Checkbox
                      onClick={() => handleLogChange("x", !isXAxisLogTemp)}
                      checked={isXAxisLogTemp}
                    />
                  }
                  label="Logarithmic"
                />
              </div>
            </div>
          </div>
          <div className="divider vertical" />
          <div className="y-axis">
            <div className="axisName">Y Axis</div>
            <div className="axisValues">
              <div className="axisValueSet">
                <div className="axisValueTitle">Name:</div>
                <input
                  id="y-axis-name-input"
                  className="axisInput"
                  value={yAxisNameTemp}
                  onChange={handleYAxisNameChange}
                />
              </div>
              <div className="axisValueSet">
                <div className="axisValueTitle">Min:</div>
                <TextField
                  className={`rangeValEntry rangeValTextField ${
                    yAxisRangeError ? "rangeValTextFieldError" : ""
                  }`}
                  value={yAxisRangeTemp.min}
                  onChange={(e) =>
                    handleAxisRangeChange("y", "min", e.target.value)
                  }
                  // onBlur={(e) =>
                  //   handleAxisRangeBlur("y", "min", e.target.value)
                  // }
                  size="small"
                  type="number"
                  id="y-axis-min-input"
                  style={yAxisRangeError ? { border: "2px solid red" } : {}}
                />
              </div>
              <div className="axisValueSet">
                <div className="axisValueTitle">Max:</div>
                <TextField
                  className={`rangeValEntry rangeValTextField ${
                    yAxisRangeError ? "rangeValTextFieldError" : ""
                  }`}
                  value={yAxisRangeTemp.max}
                  onChange={(e) =>
                    handleAxisRangeChange("y", "max", e.target.value)
                  }
                  // onBlur={(e) =>
                  //   handleAxisRangeBlur("y", "max", e.target.value)
                  // }
                  size="small"
                  type="number"
                  id="y-axis-max-input"
                  style={yAxisRangeError ? { border: "2px solid red" } : {}}
                />
              </div>
              {type === "standard" && (
                <div className="axisValueSet">
                  <FormControlLabel
                    control={
                      <Checkbox
                        onClick={handleMultiplyBy100Change}
                        checked={multiplyBy100Temp}
                      />
                    }
                    label={
                      <div className="multiplyBy100Label">
                        <span>Multiply by 100</span>
                        <HtmlTooltip
                          title={
                            <div className="multiplyBy100Tooltip">
                              For any output that is a ratio, Speqqle uses the
                              convention of values between 0 and 1. This option
                              allows to display these outputs in percentages
                              instead. Note it multiplies any output by 100, not
                              just the ratios.
                            </div>
                          }
                        >
                          <HelpOutlineIcon className="multiplyBy100Icon" />
                        </HtmlTooltip>
                      </div>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        onClick={() =>
                          setHideYAxisUnitsTemp(!hideYAxisUnitsTemp)
                        }
                        checked={hideYAxisUnitsTemp}
                      />
                    }
                    label="Hide"
                  />
                </div>
              )}
              <div className="axisValueSet">
                <FormControlLabel
                  control={
                    <Checkbox
                      onClick={() => handleLogChange("y", !isYAxisLogTemp)}
                      checked={isYAxisLogTemp}
                    />
                  }
                  label="Logarithmic"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default OptionsModal;
